import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import ValuePropositionItems, { ValueSectionItem } from './ValuePropositionItems'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import { ATOL_REGIONS, NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function ValuePropositionBanner() {
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()

  const values = useMemo(() => {
    const getValueSectionContent = (regionCode: string): Array<ValueSectionItem> => [
      {
        key: 'bestPrice',
        heading: intl.formatMessage({
          id: '6n9FK3CQih',
          defaultMessage: 'Best price guarantee',
          description: 'Best price guarantee text',
        }),
        body: intl.formatMessage({
          id: 'fMGjVchBuT',
          defaultMessage: 'We negotiate the best prices so you don\'t have to. If you find a better price, we\'ll match it.',
          description: 'Body text for best price guarantee value proposition',
        }),
        icon: <LineUsdCircleIcon />,
        show: !!getStaticPagesByType(['bestPrice']).length,
        link: {
          text: intl.formatMessage({
            id: 'EZ9tX6Rh1T',
            defaultMessage: 'Learn more',
            description: 'Link text to learn more about a feature',
          }),
          href: '/best-price-guarantee',
        },
      },
      {
        key: 'atolProtected',
        heading: intl.formatMessage({
          id: 'gVdZ49BRbj',
          defaultMessage: 'ATOL protected',
          description: 'Heading for ATOL protection value proposition',
        }),
        body: intl.formatMessage({
          id: 'LvQ2Z4M2o6',
          defaultMessage: 'Book your trip with confidence! ATOL protection provided for eligible flights and flight inclusive holidays.',
          description: 'Body text for ATOL protection value proposition',
        }),
        icon: <LineCheckCircleIcon />,
        show: ATOL_REGIONS.includes(regionCode),
      },
      {
        key: 'customerService',
        heading: intl.formatMessage({
          id: 'flurzEXImO',
          defaultMessage: '24/7 Customer service',
          description: 'Heading for 24/7 customer service value proposition',
        }),
        body: intl.formatMessage({
          id: 'g8NIf64Ybp',
          defaultMessage: 'Our dedicated team is available online or on the phone anytime, whenever you need us.',
          description: 'Body text for 24/7 customer service value proposition',
        }),
        icon: <LinePhoneIcon />,
        show: !NO_24_7_SUPPORT_REGIONS.includes(regionCode),
      },
      {
        key: 'flexibleCancellation',
        heading: intl.formatMessage({
          id: 'yrCK1lFC3A',
          defaultMessage: 'Flexible bookings',
          description: 'Heading for flexible bookings value proposition',
        }),
        body: intl.formatMessage({
          id: 'HHwMAEqkkH',
          defaultMessage: 'Travel with peace of mind with our flexible hotel cancellation and date change policies.',
          description: 'Body text for flexible bookings value proposition',
        }),
        icon: <LineRefundIcon />,
        show: true,
      },
    ]
    return getValueSectionContent(currentRegionCode).filter(value => value.show)
  }, [currentRegionCode, intl])

  const headingText = intl.formatMessage({
    id: 'EO5O0Iso/9',
    defaultMessage: 'Why you should book with us',
    description: 'Heading for value proposition banner section',
  })

  return (
    <Pane>
      <StyledLayoutContainer>
        <VerticalSpacer gap={40}>
          <Heading variant="heading2">{headingText}</Heading>
          <ValuePropositionItems values={values} />
        </VerticalSpacer>
      </StyledLayoutContainer>
    </Pane>
  )
}

export default React.memo(ValuePropositionBanner)
